// Per-event access — who may see a given event (migration 0032).
//
// No rows for a user means "no restriction beyond their rayon". One or more
// rows means "only these events". Users holding events.edit are never
// restricted, whatever rows say.

import { supabase } from '@/lib/supabase';

export interface EventUserAccess {
  eventId: string;
  userId: string;
  grantedBy: string | null;
  createdAt: number;
}

export async function listEventUsers(eventId: string): Promise<EventUserAccess[]> {
  const { data, error } = await supabase
    .from('event_users')
    .select('*')
    .eq('event_id', eventId);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    eventId: r.event_id,
    userId: r.user_id,
    grantedBy: r.granted_by,
    createdAt: new Date(r.created_at).getTime(),
  }));
}

/** Make the event's access list exactly `userIds` — inserts and deletes the difference. */
export async function setEventUsers(eventId: string, userIds: string[]): Promise<void> {
  const current = await listEventUsers(eventId);
  const want = new Set(userIds);
  const have = new Set(current.map((r) => r.userId));
  const toRemove = current.filter((r) => !want.has(r.userId)).map((r) => r.userId);
  const toAdd = userIds.filter((id) => !have.has(id));

  if (toRemove.length > 0) {
    const { error } = await supabase
      .from('event_users')
      .delete()
      .eq('event_id', eventId)
      .in('user_id', toRemove);
    if (error) throw new Error(error.message);
  }
  if (toAdd.length > 0) {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from('event_users').insert(
      toAdd.map((userId) => ({ event_id: eventId, user_id: userId, granted_by: auth.user?.id ?? null })),
    );
    if (error) throw new Error(error.message);
  }
}
